import {FlexColumn, Text} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import type {RunOutcome} from './novaInvocationStore';

export interface FailurePresentationProps {
	outcome: RunOutcome;
	// When the run was refused, as the saved record states it. Only the out-of-credits message reads
	// it, to name the reset that applied to that run; absent, the reset is dated from now.
	ranAt?: string | null;
	// Whether the surface around this one is offering a way to run the work again. It owns the
	// control — the retry sits in the card's action row, beside Discard — and this owns only the
	// words, so all this changes here is which closing line is true: a sentence pointing at the
	// task's own control is the wrong thing to leave beside a button that does it from here.
	retryOffered?: boolean;
	// Whether the reader could start another run on this task at all. The closing sentence below
	// sends them to the task's own control; on a locked task, or for a person without permission to
	// edit it, that control is withheld, so the one instruction on screen would be one they cannot
	// follow. Absent means they can, which is what every existing caller means by not passing it.
	canInvokeAgain?: boolean;
}

// Whether a failure is one a retry could get past. Every cause is, except an exhausted credit
// pool: the same run would be refused the same way until the pool resets, so the date it comes
// back is the only honest thing to offer. Exported because the control lives in the card's action
// row, which has to ask the same question this component's own out-of-credits branch answers.
export function failureInvitesRetry(outcome: RunOutcome): boolean {
	return outcome.kind === 'failed' && outcome.cause !== 'out_of_credits';
}

// The AI credit pool resets on the 1st of each calendar month in UTC, whatever the account's billing
// date. graphql masks the gateway's own refusal text, so the date is not in the response - this is
// the documented rule it would have stated. Dated from the run rather than today, so a record read
// back in a later month names the reset that applied to it.
export function creditResetDateFor(ranAt?: string | null): Date {
	const parsed = ranAt ? new Date(ranAt) : null;
	const from = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
	return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1));
}

const CAUSE_MESSAGE_ID: Record<string, string> = {
	missing_access: 'nova_invocation.failure.cause.missing_access',
	timeout: 'nova_invocation.failure.cause.timeout',
	unavailable_model: 'nova_invocation.failure.cause.unavailable_model',
	permission: 'nova_invocation.failure.cause.permission',
	document_processing: 'nova_invocation.failure.cause.document_processing',
	at_capacity: 'nova_invocation.failure.cause.at_capacity',
};

// Self-contained and prop-driven, matching RoutingDisclosure/BasedOnList. Rendered inside a
// conversation turn, which supplies the panel around it - this owns the words, not the surface they
// sit on, so the turn can tint its own card without this knowing anything about colour.
//
// Two tiers, not four equal lines: what happened reads as the sentence, and why it happened and what
// it means for the task sit under it as supporting detail. A failure is read at a glance by someone
// who wanted an answer, and a flat block of same-weight lines makes them read all of it to find out
// which line is the news.
export const FailurePresentation = (props: FailurePresentationProps): JSX.Element | null => {
	const {formatMessage, formatDate} = useIntl();
	const {outcome} = props;

	if (outcome.kind === 'succeeded') return null;

	// Not a failure, and not offered as one: no "couldn't finish", and no invitation to try again
	// that cannot work until the pool resets. Worded as chat words the same refusal, so one pool is
	// not described two ways to the same customer.
	//
	// This returns ahead of the failure block below, which is what keeps Try again away from it: a
	// retry here would spend nothing and fail the same way, so the only honest thing on screen is
	// the date the pool comes back.
	if (outcome.kind === 'failed' && outcome.cause === 'out_of_credits') {
		const resetDate = formatDate(creditResetDateFor(props.ranAt), {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			timeZone: 'UTC',
		});
		return (
			<FlexColumn data-cy="nova-out-of-credits" gap="s">
				<Text size="2">{formatMessage({id: 'nova_invocation.out_of_credits'}, {resetDate})}</Text>
			</FlexColumn>
		);
	}

	if (outcome.kind === 'cancelled') {
		return (
			<FlexColumn data-cy="nova-cancelled" gap="s">
				<Text size="2">{formatMessage({id: 'nova_invocation.cancelled.title'})}</Text>
				<Text size="2">{formatMessage({id: 'nova_invocation.cancelled.nothing_posted'})}</Text>
			</FlexColumn>
		);
	}

	const causeMessageId = CAUSE_MESSAGE_ID[outcome.cause] ?? 'nova_invocation.failure.cause.generic';
	return (
		<FlexColumn data-cy="nova-failure" gap="s">
			{/* Every line in the size an answer is set in, which is this size and not the body default.
			    A run that failed is Nova speaking in the same panel about the same task, and setting
			    the lines under the first one smaller made the failure read as a footnote to an answer
			    that is not there. The panel states what went wrong by its colour; the words it states
			    it in are an answer's. */}
			<Text size="2">{formatMessage({id: 'nova_invocation.failure.title'})}</Text>
			<Text size="2">{formatMessage({id: causeMessageId})}</Text>
			<Text size="2">{formatMessage({id: 'nova_invocation.failure.nothing_posted'})}</Text>
			{/* Stated in words only where nothing else offers the way back, and only to someone who
			    could take it. Never alongside the button - this sends the reader to the task's own
			    control, which is the wrong instruction to leave beside one that does it from here -
			    and never to a reader that control is withheld from, which would be an instruction
			    they cannot follow. Silence is the honest third state: what happened and that nothing
			    was posted still stand above it. */}
			{!props.retryOffered && props.canInvokeAgain !== false && (
				<Text size="2">{formatMessage({id: 'nova_invocation.failure.invoke_again'})}</Text>
			)}
		</FlexColumn>
	);
};
